/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */


interface Toast { id: string; title: string; description?: string; variant?: string; action?: string }

const styles = css`
  :host { bottom: 16px; display: flex; flex-direction: column; gap: 8px; max-width: calc(100vw - 32px); pointer-events: none; position: fixed; right: 16px; width: 356px; z-index: 100; }
  .stack { display: contents; }
  .toast { align-items: flex-start; background: var(--popover); border: 1px solid var(--border); border-radius: var(--radius-lg, 10px); box-shadow: 0 10px 28px rgb(0 0 0 / 14%); color: var(--popover-foreground); display: grid; gap: 2px 12px; grid-template-columns: 1fr auto; padding: 14px 16px; pointer-events: auto; }
  .title { font-size: 0.875rem; font-weight: 600; line-height: 1.3; }
  .description { color: var(--muted-foreground); font-size: 0.8125rem; grid-column: 1; line-height: 1.4; }
  .description:empty, .action:empty { display: none; }
  .action { align-items: center; background: var(--primary); border: 0; border-radius: var(--radius-sm, 6px); color: var(--primary-foreground); cursor: pointer; display: inline-flex; font: inherit; font-size: 0.75rem; font-weight: 500; grid-column: 2; grid-row: 1 / span 2; height: 28px; padding: 0 10px; }
  .toast[data-variant="error"] { border-color: color-mix(in oklab, var(--destructive) 30%, var(--border)); }
  @media (max-width: 480px) { :host { inset: auto 16px 16px; width: auto; } }
`;

export class AppSonner extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSonner)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["toasts","data-toasts"];

  #toasts = [];
  #toastsSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get toasts() {
    return this.#toasts;
  }

  set toasts(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#toasts, next)) return;
    this.#toasts = next;
    this.#toastsSet = true;
    this.#updateBindings('toasts');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-toasts')) this.toasts = this.dataset.toasts;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.toasts = props.toasts;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#toastsSet) this.toasts = this.dataset.toasts;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'toasts':
      case 'data-toasts':
        this.toasts = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const toastItems = this.#toasts.map((toast) => `<div class="toast" data-variant="${this.#escAttr(toast.variant ?? "default")}" role="status"><div class="title">${this.#escText(toast.title)}</div><div class="description">${this.#escText(toast.description ?? "")}</div><button class="action" data-toast-id="${this.#escAttr(toast.id)}" type="button">${this.#escText(toast.action ?? "")}</button></div>`).join("");
    return `<style>${this.#escText(styles)}</style><div class="stack" data-argon="2065"><!--argon-text:0-->${toastItems}<!--/argon-text:0--></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const toastItems = this.#toasts.map((toast) => `<div class="toast" data-variant="${this.#escAttr(toast.variant ?? "default")}" role="status"><div class="title">${this.#escText(toast.title)}</div><div class="description">${this.#escText(toast.description ?? "")}</div><button class="action" data-toast-id="${this.#escAttr(toast.id)}" type="button">${this.#escText(toast.action ?? "")}</button></div>`).join("");
    if (prop === undefined || prop === 'toasts') {
      const __argonNext = `${toastItems}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2065').forEach(el => this.#listen(el, '2065', 'click', (event: Event) => { const action = (event.target as Element).closest<HTMLElement>("[data-toast-id]"); if (action)
    this.dispatchEvent(new CustomEvent("toast-action", { bubbles: true, composed: true, detail: { id: action.dataset.toastId ?? "" } })); }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
